import { useEffect, useId, useMemo, useState } from "react";
import type { KeyboardEvent } from "react";


export type FilterInputOption = {
  value: string;
  label: string;
  detail?: string;
  keywords?: string[];
};

export function FilterInput({
  id,
  label,
  query,
  options,
  onQueryChange,
  onSelect,
  onInputBlur,
  placeholder,
  emptyText = "没有匹配项",
  maxVisibleOptions = 12,
  disabled = false
}: {
  id: string;
  label?: string;
  query: string;
  options: FilterInputOption[];
  onQueryChange: (query: string) => void;
  onSelect: (option: FilterInputOption) => void;
  onInputBlur?: () => void;
  placeholder?: string;
  emptyText?: string;
  maxVisibleOptions?: number;
  disabled?: boolean;
}) {
  const reactId = useId();
  const inputId = `${id}-${reactId}`;
  const listId = `${inputId}-listbox`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const visibleOptions = useMemo(() => {
    const normalized = normalizeFilterText(query);
    const candidates = filteredOptions(options, normalized);
    return candidates.slice(0, maxVisibleOptions);
  }, [maxVisibleOptions, options, query]);
  const hasOptions = visibleOptions.length > 0;

  useEffect(() => {
    setActiveIndex(0);
  }, [query, visibleOptions.length]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const selectOption = (option: FilterInputOption) => {
    if (disabled) return;
    onSelect(option);
    setOpen(false);
  };

  const renderOptionMenu = () => {
    if (!hasOptions) {
      return <div className="filter-input-empty">{emptyText}</div>;
    }
    return visibleOptions.map((option, index) => (
      <button
        key={option.value}
        id={`${listId}-${option.value}`}
        className={filterInputOptionClassName(index === activeIndex)}
        type="button"
        role="option"
        aria-selected={index === activeIndex}
        onMouseEnter={() => setActiveIndex(index)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => selectOption(option)}
      >
        <span>{option.label}</span>
        {option.detail && <em>{option.detail}</em>}
      </button>
    ));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      if (hasOptions) setActiveIndex((index) => Math.min(index + 1, visibleOptions.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      if (hasOptions) setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === "Enter" && open && hasOptions) {
      event.preventDefault();
      selectOption(visibleOptions[activeIndex]);
      return;
    }
    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="filter-input">
      {label && <label htmlFor={inputId}>{label}</label>}
      <div className="filter-input-anchor">
        <input
          id={inputId}
          value={query}
          disabled={disabled}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onChange={(event) => {
            if (disabled) return;
            onQueryChange(event.target.value);
            setOpen(true);
          }}
          onBlur={() => {
            if (disabled) {
              setOpen(false);
              return;
            }
            window.setTimeout(() => setOpen(false), 80);
            onInputBlur?.();
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open && !disabled}
          aria-controls={listId}
          aria-activedescendant={activeDescendantId(open, disabled, hasOptions, listId, visibleOptions[activeIndex])}
        />
        {open && !disabled && (
          <div className="filter-input-menu" id={listId} role="listbox">
            {renderOptionMenu()}
          </div>
        )}
      </div>
    </div>
  );
}

export function normalizeFilterText(value: string): string {
  return value.trim().toUpperCase();
}

function filterOptionText(option: FilterInputOption): string {
  return normalizeFilterText([option.value, option.label, option.detail, ...(option.keywords ?? [])].filter(Boolean).join(" "));
}

function filteredOptions(options: FilterInputOption[], normalizedQuery: string): FilterInputOption[] {
  if (!normalizedQuery) {
    return options;
  }
  return options.filter((option) => filterOptionText(option).includes(normalizedQuery));
}

function activeDescendantId(
  open: boolean,
  disabled: boolean,
  hasOptions: boolean,
  listId: string,
  activeOption: FilterInputOption | undefined
): string | undefined {
  if (!open || disabled || !hasOptions || !activeOption) {
    return undefined;
  }
  return `${listId}-${activeOption.value}`;
}

function filterInputOptionClassName(active: boolean): string {
  if (active) {
    return "filter-input-option active";
  }
  return "filter-input-option";
}
